import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

// ==================== CLICK TO GO (CTG) INVITATIONS ====================

// 1. Send Invite via JSON (POST /api/admin/invite)
export const sendInvite = createAsyncThunk(
    'ctg/sendInvite',
    async ({ emailSend, role }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        const adminId = localStorage.getItem('ID');
        try {
            const response = await axiosInstance.post(`${baseURL}/admin/invite?adminId=${adminId}`, {
                emailSend,
                role
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data || error.message || 'Failed to send invitation'
            });
        }
    }
);

// 2a. Check Invitation Excel format (POST /api/admin/check-invite-excel-format)
// Called immediately on file selection in the bulk-upload modal so we tell the
// user whether the file structure is acceptable before they hit Submit.
export const checkInviteExcelFormat = createAsyncThunk(
    'ctg/checkInviteExcelFormat',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(
                `${baseURL}/admin/check-invite-excel-format`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue({
                valid: false,
                error: error.response?.data?.message || error.message || 'Invalid Excel format',
                errors: error.response?.data?.errors
            });
        }
    }
);

// 2. Send Invite via Excel File (POST /api/admin/invite-excel)
export const sendInviteExcel = createAsyncThunk(
    'ctg/sendInviteExcel',
    async ({ file, role }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        const adminId = localStorage.getItem('ID');
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('role', role);

            const response = await axiosInstance.post(
                `${baseURL}/admin/invite-excel?adminId=${adminId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data || error.message || 'Failed to upload Excel file'
            });
        }
    }
);

// 3. Get All Invites (GET /api/admin/invitations?role=doctor&page=1&limit=10)
export const getAllInvites = createAsyncThunk(
    'ctg/getAllInvites',
    async ({ role, page = 1, limit = 5 }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/admin/invitations?role=${role}&page=${page}&limit=${limit}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data || error.message || 'Failed to fetch invitations'
            });
        }
    }
);

// 4. Reinvite (POST /api/admin/reinvite)
export const reinvite = createAsyncThunk(
    'ctg/reinvite',
    async ({ emailSend, role }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        const adminId = localStorage.getItem('ID');
        try {
            const response = await axiosInstance.post(`${baseURL}/admin/reinvite?adminId=${adminId}`, {
                emailSend,
                role
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data || error.message || 'Failed to reinvite'
            });
        }
    }
);

// 5. Get Email and Name by Invitation Code (GET /api/admin/invitation-by-code?invitationCode=abc123xyz)
export const getEmailName = createAsyncThunk(
    'ctg/getEmailName',
    async ({ invitationCode }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/admin/invitation-by-code?invitationCode=${invitationCode}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data || error.message || 'Failed to fetch invitation details'
            });
        }
    }
);

// 6. Download Sample Excel File (GET /admin/download-sample-excel)
export const demoExcelDownload = createAsyncThunk(
    'ctg/demoExcelDownload',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/admin/download-sample-excel`, {
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data || error.message || 'Failed to download sample Excel file'
            });
        }
    }
);

// ==================== MOC PATIENT INVITES (Super Admin) ====================
// /api/moc-admin/invites — super-admin-only. The email carries the MOC PATIENT
// APP's registration link (…/#/register); the invited person self-registers a
// global Mitra One Care account and no tenant/patient row is created by the
// invite. Contracts (payloads, summary shape, statuses) mirror the ClickToGo
// endpoints above, so these thunks reuse the same state fields and reducers.

// 7. Send MOC patient invite (POST /api/moc-admin/invites)
export const sendMocInvite = createAsyncThunk(
    'ctg/sendMocInvite',
    async ({ emailSend }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/moc-admin/invites`, { emailSend });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data || error.message || 'Failed to send invitation'
            });
        }
    }
);

// 8. Check MOC invite Excel format (POST /api/moc-admin/invites/check-excel-format)
export const checkMocInviteExcelFormat = createAsyncThunk(
    'ctg/checkMocInviteExcelFormat',
    async (file, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(
                `${baseURL}/moc-admin/invites/check-excel-format`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue({
                valid: false,
                error: error.response?.data?.message || error.message || 'Invalid Excel format',
                errors: error.response?.data?.errors
            });
        }
    }
);

// 9. Bulk MOC patient invites via Excel (POST /api/moc-admin/invites/excel)
export const sendMocInviteExcel = createAsyncThunk(
    'ctg/sendMocInviteExcel',
    async ({ file }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await axiosInstance.post(
                `${baseURL}/moc-admin/invites/excel`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data || error.message || 'Failed to upload Excel file'
            });
        }
    }
);

// 10. List MOC patient invites (GET /api/moc-admin/invites?page=1&limit=10)
// Rows whose email has since registered a MOC account are excluded server-side,
// so everything listed is pending — same convention as /admin/invitations.
export const getMocInvites = createAsyncThunk(
    'ctg/getMocInvites',
    async ({ page = 1, limit = 5 } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`${baseURL}/moc-admin/invites?page=${page}&limit=${limit}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data || error.message || 'Failed to fetch invitations'
            });
        }
    }
);

// 11. Resend a MOC patient invite (POST /api/moc-admin/invites/reinvite)
export const mocReinvite = createAsyncThunk(
    'ctg/mocReinvite',
    async ({ emailSend }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/moc-admin/invites/reinvite`, { emailSend });
            return response.data;
        } catch (error) {
            return rejectWithValue({
                error: error.response?.data || error.message || 'Failed to reinvite'
            });
        }
    }
);

// Initial state
const initialState = {
    isLoading: false,
    isUploading: false,
    isLoadingInvites: false,
    isReinviting: false,
    isLoadingEmailName: false,
    isDownloadingExcel: false,
    error: null,
    success: null,
    inviteResponse: null,
    excelResponse: null,
    reinviteResponse: null,
    emailNameData: null,
    invitations: [],
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
};

// Slice
const ctgSlice = createSlice({
    name: 'ctg',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = null;
        },
        clearResponse: (state) => {
            state.inviteResponse = null;
            state.excelResponse = null;
        },
    },
    extraReducers: (builder) => {
        // Send Invite (JSON)
        builder
            .addCase(sendInvite.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.success = null;
            })
            .addCase(sendInvite.fulfilled, (state, action) => {
                state.isLoading = false;
                state.success = true;
                state.inviteResponse = action.payload;
                state.error = null;
            })
            .addCase(sendInvite.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.error || 'Failed to send invitation';
                state.success = false;
            });

        // Send Invite Excel
        builder
            .addCase(sendInviteExcel.pending, (state) => {
                state.isUploading = true;
                state.error = null;
                state.success = null;
            })
            .addCase(sendInviteExcel.fulfilled, (state, action) => {
                state.isUploading = false;
                state.success = true;
                state.excelResponse = action.payload;
                state.error = null;
            })
            .addCase(sendInviteExcel.rejected, (state, action) => {
                state.isUploading = false;
                state.error = action.payload?.error || 'Failed to upload Excel file';
                state.success = false;
            });

        // Get All Invites
        builder
            .addCase(getAllInvites.pending, (state) => {
                state.isLoadingInvites = true;
                state.error = null;
            })
            .addCase(getAllInvites.fulfilled, (state, action) => {
                state.isLoadingInvites = false;
                state.invitations = action.payload?.invitations || [];
                state.total = action.payload?.total || 0;
                state.page = action.payload?.page || 1;
                state.limit = action.payload?.limit || 10;
                state.totalPages = action.payload?.totalPages || 1;
                state.error = null;
            })
            .addCase(getAllInvites.rejected, (state, action) => {
                state.isLoadingInvites = false;
                state.error = action.payload?.error || 'Failed to fetch invitations';
                state.invitations = [];
                state.total = 0;
                state.page = 1;
                state.limit = 10;
                state.totalPages = 1;
            });

        // Reinvite
        builder
            .addCase(reinvite.pending, (state) => {
                state.isReinviting = true;
                state.error = null;
                state.success = null;
            })
            .addCase(reinvite.fulfilled, (state, action) => {
                state.isReinviting = false;
                state.success = true;
                state.reinviteResponse = action.payload;
                state.error = null;
            })
            .addCase(reinvite.rejected, (state, action) => {
                state.isReinviting = false;
                state.error = action.payload?.error || 'Failed to reinvite';
                state.success = false;
            });

        // Get Email Name by Invitation Code
        builder
            .addCase(getEmailName.pending, (state) => {
                state.isLoadingEmailName = true;
                state.error = null;
                state.emailNameData = null;
            })
            .addCase(getEmailName.fulfilled, (state, action) => {
                state.isLoadingEmailName = false;
                state.emailNameData = action.payload?.data || null;
                state.error = null;
            })
            .addCase(getEmailName.rejected, (state, action) => {
                state.isLoadingEmailName = false;
                state.error = action.payload?.error || 'Failed to fetch invitation details';
                state.emailNameData = null;
            });

        // MOC patient invite (single) — same lifecycle fields as sendInvite.
        builder
            .addCase(sendMocInvite.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.success = null;
            })
            .addCase(sendMocInvite.fulfilled, (state, action) => {
                state.isLoading = false;
                state.success = true;
                state.inviteResponse = action.payload;
                state.error = null;
            })
            .addCase(sendMocInvite.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.error || 'Failed to send invitation';
                state.success = false;
            });

        // MOC patient invite via Excel — same lifecycle fields as sendInviteExcel.
        builder
            .addCase(sendMocInviteExcel.pending, (state) => {
                state.isUploading = true;
                state.error = null;
                state.success = null;
            })
            .addCase(sendMocInviteExcel.fulfilled, (state, action) => {
                state.isUploading = false;
                state.success = true;
                state.excelResponse = action.payload;
                state.error = null;
            })
            .addCase(sendMocInviteExcel.rejected, (state, action) => {
                state.isUploading = false;
                state.error = action.payload?.error || 'Failed to upload Excel file';
                state.success = false;
            });

        // MOC invites list — same table fields as getAllInvites.
        builder
            .addCase(getMocInvites.pending, (state) => {
                state.isLoadingInvites = true;
                state.error = null;
            })
            .addCase(getMocInvites.fulfilled, (state, action) => {
                state.isLoadingInvites = false;
                state.invitations = action.payload?.invitations || [];
                state.total = action.payload?.total || 0;
                state.page = action.payload?.page || 1;
                state.limit = action.payload?.limit || 10;
                state.totalPages = action.payload?.totalPages || 1;
                state.error = null;
            })
            .addCase(getMocInvites.rejected, (state, action) => {
                state.isLoadingInvites = false;
                state.error = action.payload?.error || 'Failed to fetch invitations';
                state.invitations = [];
                state.total = 0;
                state.page = 1;
                state.limit = 10;
                state.totalPages = 1;
            });

        // MOC reinvite — same lifecycle fields as reinvite.
        builder
            .addCase(mocReinvite.pending, (state) => {
                state.isReinviting = true;
                state.error = null;
                state.success = null;
            })
            .addCase(mocReinvite.fulfilled, (state, action) => {
                state.isReinviting = false;
                state.success = true;
                state.reinviteResponse = action.payload;
                state.error = null;
            })
            .addCase(mocReinvite.rejected, (state, action) => {
                state.isReinviting = false;
                state.error = action.payload?.error || 'Failed to reinvite';
                state.success = false;
            });

        // Download Sample Excel
        builder
            .addCase(demoExcelDownload.pending, (state) => {
                state.isDownloadingExcel = true;
                state.error = null;
            })
            .addCase(demoExcelDownload.fulfilled, (state) => {
                state.isDownloadingExcel = false;
                state.error = null;
            })
            .addCase(demoExcelDownload.rejected, (state, action) => {
                state.isDownloadingExcel = false;
                state.error = action.payload?.error || 'Failed to download sample Excel file';
            });
    },
});

export const { clearError, clearSuccess, clearResponse } = ctgSlice.actions;
export default ctgSlice.reducer;

